import type { BasemapView, LatLng } from "@/shared/contracts";

/** 地图基础投影：把 WGS-84 坐标映射到容器像素（尚未叠加用户的缩放/平移）。 */
export type BaseProjection = (point: LatLng) => { x: number; y: number };

export interface Size {
  width: number;
  height: number;
}

export function mercatorY(lat: number): number {
  const rad = (Math.max(-85, Math.min(85, lat)) * Math.PI) / 180;
  return Math.log(Math.tan(Math.PI / 4 + rad / 2));
}

function centered(size: Size, cx: number, cy: number, k: number): BaseProjection {
  return (p) => ({
    x: size.width / 2 + (p.lng - cx) * (Math.PI / 180) * k,
    y: size.height / 2 - (mercatorY(p.lat) - cy) * k,
  });
}

/** 在四周统一的 pad 之外，四边各再让多少像素（地图上面压着顶栏、下面压着说明时用）。 */
export interface MapInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

const NO_INSETS: MapInsets = { top: 0, right: 0, bottom: 0, left: 0 };

/**
 * 示意地图：把 bounds 适配进容器（Web Mercator，四周留白）。
 * insets（UI2-WORLD-MAP-4CD-01，世界页地区层给顶栏、说明让地方）默认四边都是 0：走原来那两行，旅途页等别的调用一字不变；
 * 不是 0 时，bounds 装进「pad 加上各边 insets」以内的内框，在内框里居中。
 */
export function schematicProjection(bounds: LatLng[], size: Size, pad = 56, insets: MapInsets = NO_INSETS): BaseProjection {
  const ys = bounds.map((p) => mercatorY(p.lat));
  const xs = bounds.map((p) => p.lng);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const spanX = Math.max(maxX - minX, 0.02) * (Math.PI / 180);
  const spanY = Math.max(maxY - minY, 0.0004);
  if (!insets.top && !insets.right && !insets.bottom && !insets.left) {
    const k = Math.min((size.width - pad * 2) / spanX, (size.height - pad * 2) / spanY);
    return centered(size, (minX + maxX) / 2, (minY + maxY) / 2, k);
  }
  // 四边各让各的：内框太小时至少 1 像素，免得翻过来
  const left = pad + insets.left;
  const top = pad + insets.top;
  const inner = { width: Math.max(1, size.width - left - pad - insets.right), height: Math.max(1, size.height - top - pad - insets.bottom) };
  const k = Math.min(inner.width / spanX, inner.height / spanY);
  const inInner = centered(inner, (minX + maxX) / 2, (minY + maxY) / 2, k);
  return (p) => {
    const q = inInner(p);
    return { x: left + q.x, y: top + q.y };
  };
}

export type AvailableBasemap = BasemapView & { image_url: string; center: LatLng; zoom: number; width: number; height: number };

export function isUsableBasemap(view: BasemapView | undefined): view is AvailableBasemap {
  return Boolean(view?.available && view.image_url && view.center && view.zoom != null && view.width && view.height);
}

/**
 * 真实底图：底图以“完整显示”方式放进容器（不裁掉图上的供应商标志与审图号），
 * 投影比例与底图像素一致，路线和车辆与街道对齐。返回投影和底图的显示倍率。
 */
export function basemapProjection(basemap: AvailableBasemap, size: Size): { base: BaseProjection; fit: number } {
  const fit = Math.min(size.width / basemap.width, size.height / basemap.height);
  const k = ((256 * 2 ** basemap.zoom) / (2 * Math.PI)) * fit;
  return { base: centered(size, basemap.center.lng, mercatorY(basemap.center.lat), k), fit };
}

/** 请求底图用的范围：四舍五入到约 10 米，避免同一路线因浮点误差重复请求。 */
export function boundsBox(bounds: LatLng[]): { south: number; west: number; north: number; east: number } | null {
  const valid = bounds.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng) && !(p.lat === 0 && p.lng === 0));
  if (valid.length === 0) return null;
  const round = (v: number) => Math.round(v * 1e4) / 1e4;
  return {
    south: round(Math.min(...valid.map((p) => p.lat))),
    west: round(Math.min(...valid.map((p) => p.lng))),
    north: round(Math.max(...valid.map((p) => p.lat))),
    east: round(Math.max(...valid.map((p) => p.lng))),
  };
}
